import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@siteflow/shared';
import { PageHeader } from '@/components/page-header';
import { SiteScopeProvider } from '@/components/sites/site-scope';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { messageForError } from '@/lib/api-client';
import { siteDetailQuery } from '@/lib/api/sites';
import { useOrgPermissions } from '@/lib/permissions';
import { CONTENT_ONLY } from '@/lib/ui-mode';
import { cn } from '@/lib/utils';

interface TabDefinition {
  label: string;
  to: string;
  permission?: string;
  exact?: boolean;
}

const TABS: TabDefinition[] = [
  { label: 'Overview', to: '/app/$orgId/sites/$siteId', exact: true },
  { label: 'Pages', to: '/app/$orgId/sites/$siteId/pages', permission: PERMISSIONS.CONTENT_VIEW },
  { label: 'Content', to: '/app/$orgId/sites/$siteId/content', permission: PERMISSIONS.CONTENT_VIEW },
  { label: 'Media', to: '/app/$orgId/sites/$siteId/media', permission: PERMISSIONS.MEDIA_VIEW },
  { label: 'Publish', to: '/app/$orgId/sites/$siteId/publish', permission: PERMISSIONS.CONTENT_VIEW },
  {
    label: 'Environments',
    to: '/app/$orgId/sites/$siteId/environments',
    permission: PERMISSIONS.SITE_VIEW,
  },
  { label: 'Domains', to: '/app/$orgId/sites/$siteId/domains', permission: PERMISSIONS.SITE_VIEW },
  {
    label: 'API keys',
    to: '/app/$orgId/sites/$siteId/api-keys',
    permission: PERMISSIONS.API_KEYS_MANAGE,
  },
  { label: 'Settings', to: '/app/$orgId/sites/$siteId/settings', permission: PERMISSIONS.SITE_VIEW },
];

function SiteLayout() {
  const { orgId, siteId } = Route.useParams();
  const { has } = useOrgPermissions(orgId);
  const isEditor = useRouterState({ select: (state) => state.location.pathname.endsWith('/edit') });
  const site = useQuery(siteDetailQuery(orgId, siteId));

  if (site.isLoading) {
    return (
      <div className="flex flex-col gap-6" aria-busy="true">
        <Skeleton className="h-16 w-72" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (site.isError || !site.data) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader eyebrow="Site" title="Site unavailable" />
        <div
          role="alert"
          className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-data text-danger"
        >
          {messageForError(site.error, 'This site could not be loaded.')}
        </div>
      </div>
    );
  }

  const visible = TABS.filter((tab) => !tab.permission || has(tab.permission));

  // The editor shell already carries the navigation, and each screen has its own
  // heading, so the site's name, slug badge and tab strip are not needed.
  // The live editor brings its own toolbar and fills the window.
  if (CONTENT_ONLY || isEditor) {
    return (
      <SiteScopeProvider site={site.data}>
        <Outlet />
      </SiteScopeProvider>
    );
  }

  return (
    <SiteScopeProvider site={site.data}>
      <div className="flex flex-col gap-6">
        <PageHeader
          eyebrow="Site"
          title={site.data.name}
          description={site.data.description ?? undefined}
          actions={
            <>
              <Badge variant="outline" className="font-mono">
                {site.data.slug}
              </Badge>
              {site.data.status === 'archived' ? <Badge variant="neutral">Archived</Badge> : null}
            </>
          }
        />

        <nav aria-label="Site sections" className="-mt-2 overflow-x-auto">
          <ul className="flex min-w-max items-center gap-1 border-b border-hairline">
            {visible.map((tab) => (
              <li key={tab.to}>
                <Link
                  to={tab.to}
                  params={{ orgId, siteId }}
                  activeOptions={{ exact: tab.exact ?? false }}
                  className={cn(
                    '-mb-px block border-b-2 border-transparent px-3 py-2 text-data font-medium',
                    'text-ink-muted transition-colors hover:text-ink',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                  )}
                  activeProps={{
                    className: 'border-accent text-ink',
                    'aria-current': 'page',
                  }}
                >
                  {tab.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <Outlet />
      </div>
    </SiteScopeProvider>
  );
}

export const Route = createFileRoute('/app/$orgId/sites/$siteId')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(siteDetailQuery(params.orgId, params.siteId)),
  component: SiteLayout,
});
